import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LoaderCircle } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LanguageProvider } from "@/lib/i18n";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import StaffHome from "@/pages/StaffHome";
import CapturePage from "@/pages/CapturePage";
import HistoryPage from "@/pages/HistoryPage";
import StaffSummaryPage from "@/pages/StaffSummaryPage";
import ReviewQueuePage from "@/pages/ReviewQueuePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import EnrolPage from "@/pages/EnrolPage";
import AdminPunchesPage from "@/pages/AdminPunchesPage";
import AdminAuditPage from "@/pages/AdminAuditPage";
import AdminActivityPage from "@/pages/AdminActivityPage";
import AdminShiftsPage from "@/pages/AdminShiftsPage";
import AdminLocationsPage from "@/pages/AdminLocationsPage";
import SystemHealthPage from "@/pages/SystemHealthPage";
import AdminOverridesPage from "@/pages/AdminOverridesPage";
import ManualPunchesPage from "@/pages/ManualPunchesPage";
import ReportsPage from "@/pages/ReportsPage";
import PdpaConsentPage from "@/pages/PdpaConsentPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

const Booting = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
  </div>
);

function RequireAuth({ children, roles }) {
  const { user, booting } = useAuth();
  const location = useLocation();
  if (booting) return <Booting />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  // PDPA 2010: staff must record biometric consent before anything else
  if (user.role === "staff" && !user.pdpaAcceptedAt &&
      !["/pdpa", "/change-password"].includes(location.pathname)) {
    return <Navigate to="/pdpa" replace />;
  }
  // supervisors/admins punch too (Batch 27): the punch flow requires PDPA
  // consent, but the rest of their console stays accessible without it
  if (user.role !== "staff" && !user.pdpaAcceptedAt &&
      location.pathname.startsWith("/staff")) {
    return <Navigate to="/pdpa" replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user, booting } = useAuth();
  if (booting) return <Booting />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user.role === "staff") {
    return <Navigate to={user.pdpaAcceptedAt ? "/staff" : "/pdpa"} replace />;
  }
  if (user.role === "supervisor") return <Navigate to="/review" replace />;
  return <Navigate to="/admin/punches" replace />;
}

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
              <Route path="/pdpa" element={<RequireAuth><PdpaConsentPage /></RequireAuth>} />
              <Route path="/staff" element={<RequireAuth roles={["staff", "supervisor", "admin"]}><StaffHome /></RequireAuth>} />
              <Route path="/staff/capture/:type" element={<RequireAuth roles={["staff", "supervisor", "admin"]}><CapturePage /></RequireAuth>} />
              <Route path="/staff/history" element={<RequireAuth roles={["staff", "supervisor", "admin"]}><HistoryPage /></RequireAuth>} />
              <Route path="/staff/summary" element={<RequireAuth roles={["staff", "supervisor", "admin"]}><StaffSummaryPage /></RequireAuth>} />
              <Route path="/review" element={<RequireAuth roles={["supervisor", "admin"]}><ReviewQueuePage /></RequireAuth>} />
              <Route path="/admin/users" element={<RequireAuth roles={["admin", "supervisor"]}><AdminUsersPage /></RequireAuth>} />
              <Route path="/admin/users/:userId/enrol" element={<RequireAuth roles={["admin", "supervisor"]}><EnrolPage /></RequireAuth>} />
              <Route path="/admin/punches" element={<RequireAuth roles={["admin"]}><AdminPunchesPage /></RequireAuth>} />
              <Route path="/admin/audit" element={<RequireAuth roles={["admin"]}><AdminAuditPage /></RequireAuth>} />
              <Route path="/admin/activity" element={<RequireAuth roles={["admin"]}><AdminActivityPage /></RequireAuth>} />
              <Route path="/admin/shifts" element={<RequireAuth roles={["admin", "supervisor"]}><AdminShiftsPage /></RequireAuth>} />
              <Route path="/admin/overrides" element={<RequireAuth roles={["admin"]}><AdminOverridesPage /></RequireAuth>} />
              <Route path="/admin/manual-punches" element={<RequireAuth roles={["admin", "supervisor"]}><ManualPunchesPage /></RequireAuth>} />
              <Route path="/admin/locations" element={<RequireAuth roles={["admin", "supervisor"]}><AdminLocationsPage /></RequireAuth>} />
              <Route path="/admin/health" element={<RequireAuth roles={["admin", "supervisor"]}><SystemHealthPage /></RequireAuth>} />
              <Route path="/admin/reports" element={<RequireAuth roles={["admin", "supervisor"]}><ReportsPage /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
